import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Star, CreditCard, LogOut, User2, Search as SearchIcon, Sun, Moon, Check, Briefcase, Lock, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import StockSearch from "@/components/StockSearch";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/lib/i18n";
import { StockailyWordmark } from "@/components/brand/StockailyBrand";
import { deriveAvatarProps } from "@/lib/avatar";

export default function NavBar({ showSearch = true }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Slice 1 (2026-02-14) — privacy-minimal deterministic avatar
  // fallback.  Google-authenticated users continue to display their
  // real profile picture; email/password users (and Google users
  // whose remote image fails to load) receive a Stockaily-branded
  // initials chip in a deterministic color.  See lib/avatar.js.
  const { initials, color: avatarColor } = deriveAvatarProps(user);
  const [pictureFailed, setPictureFailed] = useState(false);
  const showRemotePicture = !!user?.picture && !pictureFailed;
  // Reset the failed flag whenever the picture URL changes so a new
  // login (or a user-uploaded picture in a future slice) is retried.
  React.useEffect(() => { setPictureFailed(false); }, [user?.picture]);
  const ThemeIcon = theme === "light" ? Sun : Moon;

  return (
    <header
      data-testid="app-navbar"
      className="app-dark-surface sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border/60"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center gap-2 sm:gap-3">
        <Link to="/" className="flex items-center gap-2 group min-w-0 shrink-0" data-testid="nav-logo-link" aria-label="Stockaily home">
          {/* Stockaily brand v1.1 — transparent-alpha wordmark. Sits
              directly on the navbar surface with no visible bounding
              rectangle. Target visible size:
                mobile  ~108x22 px  (height=22, aspect 4.92:1)
                desktop ~128x26 px  (height=26)                     */}
          <span className="inline-flex items-center sm:hidden">
            <StockailyWordmark height={22} />
          </span>
          <span className="hidden sm:inline-flex items-center">
            <StockailyWordmark height={26} />
          </span>
        </Link>

        <div className="flex-1 hidden md:flex justify-center px-6">
          {/* Phase 8.LP.LR v1.8 — Global permanent search bar removed.
              Search now lives on the dedicated /analyze landing page
              and on individual product pages. The navbar keeps a
              mobile-only search trigger below for cross-page ticker
              lookup convenience. */}
        </div>

        <nav className="ml-auto flex items-center gap-0.5 sm:gap-1">
          {/* Mobile-only search trigger so users can find tickers from any page */}
          {showSearch ? (
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              data-testid="nav-mobile-search-trigger"
              aria-label={t("nav.search_placeholder")}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-secondary/60 active:bg-secondary/80 transition-colors"
            >
              <SearchIcon className="w-4 h-4" />
            </button>
          ) : null}

          {/* Phase 8.LP.LR v1.8 — "Analyze" nav link. Stockaily's core
              product is stock analysis, so it belongs at the front of
              the nav. Subtly more prominent than Watchlist/Portfolio
              via a faint 1px teal ring on the active/idle state, but
              it stays visually part of the same nav pill system
              (same height, same font-size, same rounded shape). */}
          <NavLink
            to="/analyze"
            end
            data-testid="nav-analyze-link"
            className={({ isActive }) =>
              `hidden sm:inline-flex h-9 px-3 items-center gap-2 rounded-md text-sm hover:bg-secondary/60 ring-1 ${
                isActive
                  ? "bg-secondary/60 ring-[rgba(63,174,137,0.55)]"
                  : "ring-[rgba(63,174,137,0.20)]"
              }`
            }
          >
            <LineChart className="w-4 h-4" /> {t("nav.analyze")}
          </NavLink>
          <NavLink to="/watchlist" data-testid="nav-watchlist-link" className={({ isActive }) => `hidden sm:inline-flex h-9 px-3 items-center gap-2 rounded-md text-sm hover:bg-secondary/60 ${isActive ? "bg-secondary/60" : ""}`}>
            <Star className="w-4 h-4" /> {t("nav.watchlist")}
          </NavLink>
          {/* Phase 8.WLNAV.v1 — Portfolio nav link is always visible.
              Authorization gates what the /portfolio route renders, not
              whether the link exists. Logged-out visitors and non-Premium
              users see a controlled preview + CTA to /pricing. */}
          <NavLink to="/portfolio" data-testid="nav-portfolio-link" className={({ isActive }) => `hidden sm:inline-flex h-9 px-3 items-center gap-2 rounded-md text-sm hover:bg-secondary/60 ${isActive ? "bg-secondary/60" : ""}`}>
            <Briefcase className="w-4 h-4" /> {t("nav.portfolio")}
            {user && ((user.plan || "free").toLowerCase() !== "premium" && !user.is_admin) ? (
              <Lock className="w-3 h-3 text-[#3DEBFF]" aria-label="Premium feature" />
            ) : null}
          </NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing-link" className={({ isActive }) => `hidden sm:inline-flex h-9 px-3 items-center gap-2 rounded-md text-sm hover:bg-secondary/60 ${isActive ? "bg-secondary/60" : ""}`}>
            <CreditCard className="w-4 h-4" /> {t("nav.pricing")}
          </NavLink>

          <div className="ml-1 sm:ml-2">
            <LanguageSwitcher variant="navbar" />
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-account-menu" className="ml-1 sm:ml-2 inline-flex items-center gap-2 h-10 sm:h-9 px-2 rounded-md hover:bg-secondary/60 active:bg-secondary/80 transition-colors">
                  <Avatar
                    className="h-7 w-7"
                    data-testid="nav-account-avatar"
                    aria-label={user?.name || user?.email || "Account"}
                  >
                    {showRemotePicture ? (
                      <AvatarImage
                        src={user.picture}
                        alt={user.name || user.email || "Account"}
                        onError={() => setPictureFailed(true)}
                        data-testid="nav-account-avatar-image"
                      />
                    ) : null}
                    <AvatarFallback
                      style={{
                        backgroundColor: avatarColor.background,
                        color: avatarColor.foreground,
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                      }}
                      data-testid="nav-account-avatar-fallback"
                      data-avatar-source={showRemotePicture ? "remote" : "initials"}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm text-muted-foreground max-w-[120px] truncate">{user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-xs text-muted-foreground">{t("nav.signed_in_as")}</div>
                  <div className="text-sm font-medium truncate">{user.email}</div>
                  <div className="mt-1"><Badge variant="outline" className="text-[10px] uppercase">{user.plan}</Badge></div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/billing")}> <CreditCard className="w-4 h-4 mr-2" /> {t("nav.billing_usage")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/analyze")} data-testid="nav-account-analyze-link"> <LineChart className="w-4 h-4 mr-2" /> {t("nav.analyze")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/watchlist")}> <Star className="w-4 h-4 mr-2" /> {t("nav.watchlist")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/portfolio")} data-testid="nav-account-portfolio-link"> <Briefcase className="w-4 h-4 mr-2" /> {t("nav.portfolio")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} className="sm:hidden"> <CreditCard className="w-4 h-4 mr-2" /> {t("nav.pricing")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* Phase 7.UI.1 — Theme submenu (Dark / Light). Persists
                    to the user profile + localStorage. */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="nav-theme-trigger">
                    <ThemeIcon className="w-4 h-4 mr-2" />
                    {t("theme.label")}
                    <span className="ml-auto text-[11px] text-muted-foreground capitalize">{theme === "dark" ? t("theme.dark") : t("theme.light")}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onSelect={() => setTheme("dark")} data-testid="nav-theme-dark">
                        <Moon className="w-4 h-4 mr-2" />
                        <span>{t("theme.dark")}</span>
                        {theme === "dark" ? <Check className="w-3.5 h-3.5 ml-auto" /> : null}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setTheme("light")} data-testid="nav-theme-light">
                        <Sun className="w-4 h-4 mr-2" />
                        <span>{t("theme.light")}</span>
                        {theme === "light" ? <Check className="w-3.5 h-3.5 ml-auto" /> : null}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.log_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2 ml-1 sm:ml-2">
              {/* Phase 7.UI.1 — anonymous theme toggle. Compact icon
                  button so it doesn't clutter the navbar; preference is
                  saved to localStorage and survives refresh. */}
              <button
                type="button"
                onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                data-testid="nav-theme-toggle"
                aria-label={theme === "light" ? t("theme.switch_to_dark") : t("theme.switch_to_light")}
                title={theme === "light" ? t("theme.switch_to_dark") : t("theme.switch_to_light")}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-secondary/60 active:bg-secondary/80 transition-colors text-muted-foreground hover:text-foreground"
              >
                <ThemeIcon className="w-4 h-4" />
              </button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login-button" className="h-9 px-2 sm:px-3">
                <User2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{t("nav.log_in")}</span>
              </Button>
              <Button size="sm" onClick={() => navigate("/signup")} data-testid="nav-signup-button" className="shadow-[var(--app-shadow-elev-1)] h-9 px-3">
                {t("nav.get_started")}
              </Button>
            </div>
          )}
        </nav>
      </div>

      {/* Mobile search dialog */}
      <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
        <DialogContent className="sm:max-w-md p-4 sm:p-5 gap-3" data-testid="nav-mobile-search-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="text-base">{t("nav.search_placeholder")}</DialogTitle>
          </DialogHeader>
          <StockSearch
            testIdPrefix="nav-mobile-search"
            placeholder={t("nav.search_placeholder")}
            autoFocus
          />
        </DialogContent>
      </Dialog>
    </header>
  );
}
